// =============================================================================
// ui/tabs/TradeDetail.tsx — the six-tab trade form. Add and edit.
// -----------------------------------------------------------------------------
// Transcribed from six screenshots of beta, one per tab, on a real trade with
// data in it — NOT from reading the frozen file. §3a of the handoff is the rule
// and it has been earned twice: source-reading invented two controls beta does
// not have, because `app/index.html` still carries code for removed features and
// for the CLASSIC layout Josh does not use, and the classic branch reads first.
//
// The source was still read, for WIRING — what each control computes, what it
// stores, which rule blocks a save. The screenshots decide what EXISTS; the
// source decides what it does. That split is the whole method.
//
// ── 🚨 IT HYDRATES BEFORE IT RENDERS A FIELD (D61) ──
//
// The table row carries 23 of 48 columns. This form edits thirty-one fields, most
// of them in the other 25. A form populated from the row would show BLANK for
// notes, grade, pattern, exit model, mistake tags, tags, confluences and every
// discipline flag — and a save would then write those blanks back with the user's
// full authorisation. That is worse than the D46 failure it resembles: nothing
// refuses it, because nothing is wrong except what the user was shown.
//
// So: own read on mount, loading state until it lands, and NO FORM AT ALL if it
// fails. `AnnotateTrade` established the pattern; this is the surface it was
// established for.
//
// ⚠ A LOCAL ledger, not the app's. A display read must not grant write permission
// app-wide for a trade the user may cancel out of, and `saveTrade` hydrates again
// for its own — that second read is the write's safety property, not an
// optimisation to remove.
//
// ── 🚨 SAVING MARKS THE TRADE REVIEWED ──
//
// `commitTrade` ends `t.annotated = true`, unconditionally. Ported, because it is
// what makes this form the review surface — and SAID ON SCREEN beside the Save
// button, because it moves the trade out of Needs Review and makes its unchecked
// discipline flags start counting as "this held".
//
// ── WHAT IS DEFERRED, AND NAMED RATHER THAN OMITTED ──
//
//   the chart          Lightweight Charts + the bars-fetch edge function
//   entry criteria     resolves against pattern rule VERSIONS, a subsystem this
//                      client does not load. Existing marks are PRESERVED,
//                      because the form never sends `criteriaMet`.
//   Post to Discord    the webhook post
//
// A missing thing that says it is missing reads as unfinished. A missing thing
// that says nothing reads as lost.
// =============================================================================

import {
  useCallback, useEffect, useMemo, useState,
  type ChangeEvent, type ReactNode,
} from 'react';
import { useAccounts } from '../state/accounts';
import { useContracts } from '../state/contracts';
import { useExitModels } from '../state/exitModels';
import { usePatterns } from '../state/patterns';
import { usePreferences } from '../state/preferences';
import { useSupabase } from '../state/supabase';
import { useTabMemoryDraft } from '../state/tabMemory';
import { loadFullTrades } from '../../io/sync/load';
import { createHydrationLedger } from '../../io/sync/hydration';
import { newTradeId } from '../../io/sync/assembleImported';
import { taggablePatterns } from '../../io/sync/patterns';
import {
  deleteTrade, insertTrade, recomputeSessionDate, resolvePatternVersion,
  resolveSessionLabel, saveTrade,
  type TradeExtrasPatch, type TradePatch,
} from '../../io/sync/saveTrade';
import {
  ANNOTATION_REQUIREMENTS, GRADES, REGIMES, SESSIONS, TRADE_BOOLEANS, TRADE_FORM_TABS,
  convertRrField, deriveTrade, excursionDisplay, excursionRiskPoints,
  applyInitialRiskFlow, defaultRiskFor, fromRiskPlan, fromStop, fromTarget,
  seedRiskPlan, seedRrField, seedSessionLabel, storedRrPlanned,
  tabsNeedingAttention, unfilledRequirements, validateTradeForm,
  type PlannedTargetUnit, type TradeFormTab, type TradeFormValues,
} from '../../selectors/tradeForm';
import {
  activeConfluenceIds, confluenceRegistry, confluencesToStore, type ConfluenceNode,
} from '../../selectors/confluences';
import { parseTagRegistry, type UserTag } from '../../selectors/accounts';
import type { PatternSummary } from '../../io/sync/patterns';
import type { ExitModelSummary } from '../../io/sync/exitModels';
import { money, num } from '../format';
import { ScreenshotGrid } from '../media/ScreenshotGrid';
import { ShotCarousel } from '../media/ShotCarousel';
import { VoiceNotes } from '../media/VoiceNotes';
import { reportFailure } from '../../io/diagnostics/errorLog';

type Row = Record<string, unknown>;

const s = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const n = (v: unknown): number => {
  const x = Number(v);
  return Number.isFinite(x) ? x : 0;
};

export interface TradeDetailProps {
  /** `null` opens a BLANK form — the Add Trade path. */
  tradeId: string | null;
  /** Chronological ids of the trades in view, for the ‹ › header nav. */
  siblingIds: readonly string[];
  onNavigate: (id: string) => void;
  onSaved: () => void;
  onDeleted: () => void;
  onCancel: () => void;
}

// =============================================================================
// The hydration shell
// =============================================================================

export function TradeDetail(props: TradeDetailProps) {
  const { sb } = useSupabase();
  // ⚠ Held here, not just used below. The form seeds its state ONCE, and the
  // initial risk-plan pass snaps Stop and Target to the instrument's TICK — so a
  // contracts list that lands one render late would seed a stop of 7473.15 where
  // the tick makes it 7473.25, and nothing would ever correct it. Wrong by one
  // tick is the worst kind of wrong here: it looks like a price.
  const { contracts } = useContracts();
  const { tradeId } = props;

  const [loaded, setLoaded] = useState<Row | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setLoaded(null);
    setLoadError(null);

    // ── ADD: nothing to read. The object is constructed here, COMPLETE, which is
    // what makes `markNew` an honest ledger claim later.
    if (!tradeId) {
      setLoaded(blankTrade());
      return () => { live = false; };
    }

    void (async () => {
      try {
        // A ledger scoped to this read and thrown away with it.
        const rows = await loadFullTrades(sb, [tradeId], createHydrationLedger());
        if (!live) return;
        const t = rows[0] as unknown as Row | undefined;
        if (!t) {
          setLoadError('That trade no longer exists — it may have been deleted in another tab.');
          return;
        }
        setLoaded(t);
      } catch (e) {
        if (live) setLoadError(reportFailure('load trade', e));
      }
    })();
    return () => { live = false; };
  }, [sb, tradeId]);

  if (loadError) {
    // ⚠ NO FORM. Rendering an empty editor here is how a failed read becomes a
    // successful erase — every blank field would be saved as the user's choice.
    return (
      <div className="card trade-detail" role="dialog" aria-label="Trade detail">
        <div role="alert" className="import-alert">
          <b>Couldn’t load this trade in full, so there is nothing to edit.</b> {loadError}
        </div>
        <div className="import-row">
          <button type="button" className="btn" onClick={props.onCancel}>Close</button>
        </div>
      </div>
    );
  }

  if (!loaded || !contracts) {
    return (
      <div className="card trade-detail" role="dialog" aria-label="Trade detail">
        <p className="muted" role="status">Loading the full trade…</p>
      </div>
    );
  }

  // ⚠ KEYED ON THE RECORD (D65). Every field below seeds with `useState`, which
  // runs once — so stepping ‹ › to the next trade without a reset would leave the
  // previous trade's values in the form beside the new trade's header, with Save
  // armed. That exact shape carried one day's journal draft into another day's
  // form and would have erased a real plan.
  //
  // ⚠ WHAT ACTUALLY DOES THE RESET IS `setLoaded(null)` in the effect above, and
  // that was MEASURED rather than assumed: with the null in place, removing this
  // key leaves `Trades.test.tsx > opening a SECOND row` green. Removing both
  // fails it with the real symptom. The key stays as the narrower guard — it also
  // covers a `loaded` that goes old → new without passing through null, which is
  // what a later "avoid the loading flash" change would introduce — but the null
  // is the load-bearing half and must not be optimised away.
  return <TradeDetailForm key={tradeId ?? 'new'} {...props} trade={loaded} />;
}

/** A new trade, complete rather than partial — see `markNew`. */
function blankTrade(): Row {
  return {
    id: newTradeId(),
    accountId: null, date: todayIso(), sessionDate: null,
    timeIn: '', timeOut: '', contract: '', contractFull: null,
    side: 'long', qty: 1, entry: 0, exit: 0,
    points: 0, grossPnl: 0, fees: 0, feesAuto: true, netPnl: 0,
    maePoints: null, mfePoints: null, maeSecs: null, mfeSecs: null,
    riskPoints: null, rrPlanned: null, rrRealized: null,
    plannedStop: null, plannedTarget: null, riskAtEntry: null,
    patternId: null, exitModelId: null, grade: '', sessionLabel: '',
    marketRegime: '', newsEvent: '', mood: '', mistakeTags: [], tags: [],
    confluences: {}, booleans: {},
    controllableLossAmount: 0, screenshots: [], recordings: [], notes: '',
    annotated: true, imported: false, sourceAccount: null,
    syntheticClose: false, flagged: false, flagReasons: [],
  };
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// =============================================================================
// The form
// =============================================================================

function TradeDetailForm(
  { trade, tradeId, siblingIds, onNavigate, onSaved, onDeleted, onCancel }:
  TradeDetailProps & { trade: Row },
) {
  const { sb, userId } = useSupabase();
  const { accounts } = useAccounts();
  const { contracts } = useContracts();
  const { patterns } = usePatterns();
  const { exitModels, phase: emPhase } = useExitModels();
  const { prefs } = usePreferences();

  const isNew = tradeId == null;
  const tz = prefs?.sessionTimezone ?? 'America/New_York';

  // ── the R:R unit ───────────────────────────────────────────────────────────
  // ⚠ Seeded from `appFlags.plannedTargetUnit` and NOT written back. Flipping it
  // is a display choice; persisting it means a preferences write from inside a
  // trade save, and a failed one would be reported as a failed trade save.
  const [unit, setUnit] = useState<PlannedTargetUnit>(
    () => (prefs?.appFlags?.['plannedTargetUnit'] === 'points' ? 'points' : 'r'),
  );

  const registry = useMemo<readonly ConfluenceNode[]>(
    () => confluenceRegistry(prefs?.confluences), [prefs],
  );

  // ⚠ Resolved from the STORED contract, not from `values.contract`, because the
  // seed needs it and `values` does not exist yet. The initial risk-plan pass
  // snaps Stop and Target to this tick.
  const seedTick = (contracts ?? []).find((c) => c.symbol === s(trade['contract']))?.tickSize ?? null;

  // ── 🔴 THE DRAFT OUTLIVES THE TAB, BUT CANCEL STILL DISCARDS IT (TCR-80) ──
  //
  // This form holds the most-written prose in the app — trade notes — and
  // `AppShell` destroys the tab on every switch (D94), so walking away from
  // Trades threw the lot away with no prompt and no trace.
  //
  // ⚠ AUTOSAVE IS DELIBERATELY NOT THE FIX. Every surface in this client that
  // commits on blur is an in-page card; this is a modal with a Cancel, and
  // writing on blur would make that button a lie. It would also fire
  // `validateTradeForm` on every blur, whose failure path jumps the reader to
  // another tab mid-edit. The draft lives above the tab and only Save writes.
  // Decided by Josh, 2026-10-06.
  //
  // ⚠ Keyed per trade — and `TradeDetail` already mounts this with
  // `key={tradeId ?? 'new'}`, so stepping to a sibling gets its own slot rather
  // than inheriting the last one's edits. That is the same hazard D61 handles
  // for the journal with `key={date}`.
  //
  // ⚠ A restored draft reads as dirty, which is correct: there ARE unsaved
  // edits, so the leave guard below should fire.
  const [values, setValues, discardDraft] = useTabMemoryDraft<TradeFormValues>(
    `tradedetail:draft:${tradeId ?? 'new'}`,
    seedValues(trade, unit, prefs?.defaultRiskByInstrument ?? null, seedTick, tz),
  );
  const [tab, setTab] = useState<TradeFormTab>('ov');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Beta's `annReviewed`: pressing Done in the overlay silences the badge. */
  const [reviewedInOverlay, setReviewedInOverlay] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);

  const contract = useMemo(
    () => (contracts ?? []).find((c) => c.symbol === values.contract) ?? null,
    [contracts, values.contract],
  );
  const dpp = contract?.dollarsPerPoint ?? 0;
  const tick = contract?.tickSize ?? null;

  const derived = useMemo(() => deriveTrade(values, dpp), [values, dpp]);

  // ── the three risk-plan flows ──────────────────────────────────────────────
  //
  // ⚠ Separate handlers rather than one "recompute everything" pass. Which flow
  // runs depends on which field was touched, and a single solver has no way to
  // know — so it overwrites the number being typed. The old client needs an
  // `activeElement` guard for precisely that; this shape does not, because the
  // flow that runs never writes the field that triggered it.
  const editPlan = useCallback((p: Partial<TradeFormValues>) => {
    setValues((cur) => {
      const next = { ...cur, ...p };
      return { ...next, ...fromRiskPlan(next, unit, tick) };
    });
  }, [unit, tick]);

  const editStop = useCallback((plannedStop: number) => {
    setValues((cur) => {
      const next = { ...cur, plannedStop };
      return { ...next, ...fromStop(next, unit, tick) };
    });
  }, [unit, tick]);

  const editTarget = useCallback((plannedTarget: number) => {
    setValues((cur) => {
      const next = { ...cur, plannedTarget };
      return { ...next, ...fromTarget(next, unit) };
    });
  }, [unit]);

  const set = useCallback(<K extends keyof TradeFormValues>(k: K, v: TradeFormValues[K]) => {
    setValues((cur) => ({ ...cur, [k]: v }));
  }, []);

  const flipUnit = useCallback((to: PlannedTargetUnit) => {
    if (to === unit) return;
    setValues((cur) => {
      const rrField = convertRrField(cur.rrField, cur.riskPoints, to);
      const next = { ...cur, rrField };
      return { ...next, ...fromRiskPlan(next, to, tick) };
    });
    setUnit(to);
  }, [unit, tick]);

  // ── completeness ───────────────────────────────────────────────────────────
  const unfilled = useMemo(
    () => (reviewedInOverlay ? [] : unfilledRequirements(values)),
    [values, reviewedInOverlay],
  );
  const dotted = useMemo(
    () => (reviewedInOverlay ? new Set<TradeFormTab>() : tabsNeedingAttention(values)),
    [values, reviewedInOverlay],
  );

  // ── save ───────────────────────────────────────────────────────────────────
  const save = useCallback(async () => {
    const invalid = validateTradeForm(values, (exitModels ?? []).length > 0);
    if (invalid) { setError(invalid); setTab('su'); return; }

    setSaving(true);
    setError(null);

    const session = resolveSessionLabel(values.sessionLabel, values.date, values.timeIn, tz);
    const liveVersion = (patterns ?? []).find((p) => p.id === values.patternId)?.liveVersion ?? null;

    const patch = {
      accountId: values.accountId || null,
      date: values.date,
      sessionDate: recomputeSessionDate(values.date, values.timeIn, tz),
      contract: values.contract,
      side: values.side,
      qty: values.qty,
      timeIn: values.timeIn || null,
      timeOut: values.timeOut || null,
      entry: values.entry,
      exit: values.exit,
      fees: values.fees,
      feesAuto: values.feesAuto,
      points: derived.points,
      grossPnl: derived.grossPnl,
      netPnl: derived.netPnl,
      riskPoints: values.riskPoints,
      rrPlanned: storedRrPlanned(values, unit),
      rrRealized: derived.rrRealized,
      plannedStop: values.plannedStop,
      plannedTarget: values.plannedTarget,
      riskAtEntry: derived.riskAtEntry,
      patternId: values.patternId || null,
      exitModelId: values.exitModelId || null,
      grade: values.grade,
      sessionLabel: session.sessionLabel,
      marketRegime: values.marketRegime,
      newsEvent: values.newsEvent,
      // ⚠ Through `confluencesToStore`, never assembled here — that function is
      // what carries an orphan confluence forward through the save.
      confluences: confluencesToStore(registry, values.confluences, trade['confluences']),
      booleans: values.booleans,
      mistakeTags: [...values.mistakeTags],
      tags: [...values.tags],
      notes: values.notes,
      // ⚠ Beta's rule, ported: saving IS the review. Stated on screen.
      annotated: true,
    } as unknown as TradePatch;

    const extras: TradeExtrasPatch = {
      sessionAuto: session.sessionAuto,
      patternVersion: resolvePatternVersion(
        s(trade['patternId']) || null,
        values.patternId,
        trade['patternVersion'] == null ? null : n(trade['patternVersion']),
        liveVersion,
      ),
    };

    const ledger = createHydrationLedger();
    const res = isNew
      ? await insertTrade(sb, trade as never, patch, extras, userId, ledger)
      : await saveTrade(sb, String(tradeId), patch, extras, userId, ledger);

    setSaving(false);
    if (res.error) { setError(res.error); return; }
    discardDraft();
    onSaved();
  }, [
    values, derived, unit, tz, registry, trade, patterns, exitModels,
    sb, userId, isNew, tradeId, onSaved,
  ]);

  const remove = useCallback(async () => {
    if (!tradeId) return;
    if (!window.confirm(
      'Delete this trade permanently?\n\n'
      + 'This cannot be undone — trades have no 30-day recovery window.',
    )) return;
    setSaving(true);
    setError(null);
    const res = await deleteTrade(sb, userId, tradeId);
    setSaving(false);
    if (res.error) { setError(res.error); return; }
    discardDraft();
    onDeleted();
  }, [sb, userId, tradeId, onDeleted]);

  // ── the ‹ › nav ────────────────────────────────────────────────────────────
  //
  // ⚠ It does NOT save first, and the old client's does ("Previous trade (saves
  // current)"). Deliberate: an implicit save on a navigation button writes every
  // field of a trade the user may only have been reading, and this form's save
  // also flips `annotated` to true. Stepping through twenty trades to look at
  // them would mark all twenty reviewed. So it warns when there are edits.
  const idx = tradeId ? siblingIds.indexOf(tradeId) : -1;
  const dirty = useMemo(
    () => JSON.stringify(serialiseForDirty(values))
      !== JSON.stringify(serialiseForDirty(seedValues(trade, unit, prefs?.defaultRiskByInstrument ?? null, seedTick, tz))),
    [values, trade, unit],
  );
  /**
   * Close, asking first if there is anything to lose.
   *
   * 🔴 THE SCRIM COMMENT BELOW CLAIMED THIS ALREADY HAPPENED — "`onCancel`
   * already asks before discarding them" — AND IT DID NOT. Measured 2026-10-06:
   * all three callers (`Trades.tsx:495`, `DayOverview.tsx:800`,
   * `CoachDrill.tsx:197`) pass a bare `setEditing(null)` / `setOpenId(null)`.
   * The only prompt was the one in `goto` just below, which guards the ‹ › arrows
   * and nothing else — so a stray ✕ discarded trade notes in silence, which is
   * the loss this card exists to stop. Same sentence as `goto` uses, on purpose:
   * two wordings for one decision is how they drift apart.
   */
  const closeWithGuard = useCallback(() => {
    if (dirty && !window.confirm('You have unsaved edits on this trade. Leave them?')) return;
    discardDraft();
    onCancel();
  }, [dirty, discardDraft, onCancel]);

  const goto = (delta: number) => {
    const j = idx + delta;
    if (idx < 0 || j < 0 || j >= siblingIds.length) return;
    const next = siblingIds[j];
    if (!next) return;
    if (dirty && !window.confirm('You have unsaved edits on this trade. Leave them?')) return;
    // ⚠ The draft is dropped only once the reader has agreed to lose it — the
    // confirm above is the gate, and stepping to a sibling remounts this form
    // under a different key anyway.
    discardDraft();
    onNavigate(next);
  };

  const header = [s(trade['date']), s(trade['timeIn'])].filter(Boolean).join(' · ');
  const discordConfigured = !!(prefs?.webhooks?.['tradeDetails'] ?? '').trim();

  // ---------------------------------------------------------------------------

  return (
    /**
     * ── 🚨 A MODAL, AND IT WAS RENDERING INLINE ──
     *
     * This shipped as `<div className="card trade-detail">` dropped into the
     * Trades page between the import history and the stats strip. Beta opens it
     * as a centred modal over a dimmed page (`.modal.xwide.noheader` inside
     * `.modal-overlay`), which is what makes it read as "you are editing THIS
     * trade" rather than as more page.
     *
     * ⚠ NO CLICK-TO-CLOSE ON THE SCRIM. Every other modal in this client allows
     * it; this one holds unsaved edits to a real trade, and the ✕ and Cancel ask
     * before discarding them (see `closeWithGuard`). A stray click on the backdrop
     * must not be a quieter route to the same loss.
     *
     * ⚠ THIS USED TO SAY `onCancel` DID THE ASKING. It did not — every caller
     * passed a bare close, so until TCR-80 the sentence described a guard that
     * was not there.
     */
    <div className="modal-scrim" role="presentation">
    <div className="modal xwide trade-detail" role="dialog" aria-label="Trade detail">
      <div className="tw-header">
        <button
          type="button" className="tw-nav" onClick={() => goto(-1)}
          disabled={isNew || idx <= 0} aria-label="Previous trade"
        >‹</button>
        <button
          type="button" className="tw-nav" onClick={() => goto(1)}
          disabled={isNew || idx < 0 || idx >= siblingIds.length - 1} aria-label="Next trade"
        >›</button>
        <span className="tw-sym">{s(trade['contract']) || (isNew ? 'New trade' : '—')}</span>
        <span className="tw-when">{header}</span>
        <span className="grow" />
        <button type="button" className="tw-nav" onClick={closeWithGuard} aria-label="Close">✕</button>
      </div>

      {/* ⚠ NAMED, not omitted. An empty band where beta shows a chart reads as a
          broken chart; this reads as a chart that has not been built yet.

          ⚠ THE SCREENSHOT CAROUSEL CAME OFF THIS SENTENCE when the gallery
          shipped. D84 — the "not ported yet" copy is checked against the PAGE,
          and a band that keeps naming a surface rendering two tabs away is the
          same rot that made the Journal's card claim its own editors did not
          exist. It also claimed the reason was "signed Storage URLs", which was
          never true of either client: beta downloads the blob and calls
          `URL.createObjectURL`. */}
      <p className="muted deferred-band">
        The price chart isn’t ported yet — it needs the bar-fetch service.
        Everything below is live.
      </p>

      <div className="tw2-tabs" role="tablist">
        {TRADE_FORM_TABS.map(([id, label]) => (
          <button
            key={id} type="button" role="tab"
            className={`tw2-tab${tab === id ? ' active' : ''}`}
            aria-selected={tab === id}
            onClick={() => setTab(id)}
          >
            {label}
            {dotted.has(id) ? (
              <span className="tw2-dot" aria-label="needs annotation" title="Something here is still unfilled" />
            ) : null}
          </button>
        ))}
      </div>

      {/* ⚠ The panel is UNMOUNTED while the overlay is up. The old client gets
          this for free — its overlay physically relocates the controls out of the
          form — and without it the same field exists twice on screen, which is
          both a duplicate-label trap for tests and two inputs a user could type
          different things into. */}
      {!overlayOpen ? (
        <div className="tw2-panel active" role="tabpanel">
          {tab === 'ov' ? <OverviewTab v={values} d={derived} trade={trade} patterns={patterns} /> : null}
          {tab === 'ex' ? (
            <ExecutionTab
              v={values} d={derived} trade={trade} set={set} editPlan={editPlan}
              accounts={accounts} contracts={contracts}
            />
          ) : null}
          {tab === 'rk' ? (
            <RiskTab
              v={values} d={derived} trade={trade} unit={unit}
              editPlan={editPlan} editStop={editStop} editTarget={editTarget} flipUnit={flipUnit}
              dpp={dpp}
            />
          ) : null}
          {tab === 'su' ? (
            <SetupTab
              v={values} set={set} patterns={patterns} exitModels={exitModels}
              exitModelsPhase={emPhase} registry={registry}
            />
          ) : null}
          {tab === 'pr' ? (
            <ProcessTab v={values} d={derived} set={set} prefs={prefs} />
          ) : null}
          {tab === 'nm' ? <NotesTab v={values} set={set} /> : null}
        </div>
      ) : null}

      {error ? (
        <div role="alert" className="import-alert">
          <b>Nothing was saved.</b> {error}
        </div>
      ) : null}

      <div className="tw2-footer import-row">
        {!isNew ? (
          <button type="button" className="btn danger" disabled={saving} onClick={() => void remove()}>
            Delete
          </button>
        ) : null}
        <span className="grow" />
        {discordConfigured ? (
          <button
            type="button" className="btn" disabled
            title="Posting to Discord isn’t ported yet — it needs the webhook post path"
          >
            Post to Discord
          </button>
        ) : null}
        <button
          type="button"
          className={`btn tw2-annbtn${unfilled.length ? ' pulse' : ' done'}`}
          onClick={() => setOverlayOpen(true)}
          title="Fill in the fields still missing"
        >
          ✓ Complete Annotations{' '}
          <span className="tw2-annbadge">{unfilled.length ? `${unfilled.length} left` : '✓'}</span>
        </button>
        <button type="button" className="btn" disabled={saving} onClick={closeWithGuard}>Cancel</button>
        <button type="button" className="btn primary" disabled={saving} onClick={() => void save()}>
          {saving ? 'Saving…' : 'Save Trade'}
        </button>
      </div>

      {/* ⚠ SAID, not discovered. Saving flips `annotated`, which removes the trade
          from Needs Review and makes every unchecked box above start counting as
          "this expectation held". */}
      <p className="muted save-consequence">
        Saving marks this trade <b>reviewed</b> — it leaves Needs Review, and the
        discipline boxes you left unchecked begin counting as “that held”.
      </p>

      {overlayOpen ? (
        <CompleteAnnotationsOverlay
          v={values} set={set} editPlan={editPlan} unit={unit}
          patterns={patterns} exitModels={exitModels} registry={registry} prefs={prefs}
          d={derived}
          onSkip={() => setOverlayOpen(false)}
          onDone={() => { setReviewedInOverlay(true); setOverlayOpen(false); }}
        />
      ) : null}
    </div>
    </div>
  );
}

// =============================================================================
// Seeding
// =============================================================================

function seedValues(
  t: Row, unit: PlannedTargetUnit, defaultRisk: Record<string, unknown> | null,
  tickSize: number | null, tz: string,
): TradeFormValues {
  // ⚠ Resolved BEFORE the fields, because `rrField` under the `pts` unit is
  // derived from the risk — seeding them independently lets the two disagree.
  const plan = seedRiskPlan(
    {
      riskPoints: t['riskPoints'] == null ? null : n(t['riskPoints']),
      rrPlanned: t['rrPlanned'] == null ? null : n(t['rrPlanned']),
      plannedStop: t['plannedStop'] == null ? null : n(t['plannedStop']),
      plannedTarget: t['plannedTarget'] == null ? null : n(t['plannedTarget']),
    },
    t['entry'] == null ? null : n(t['entry']),
    defaultRiskFor(s(t['contract']), defaultRisk),
  );
  // ⚠ The seeded fields go through the initial risk-plan pass, exactly as the old
  // client does on open. Without it Planned Stop and Planned Target render EMPTY
  // beside a filled Risk on every trade that stores neither — which is most of
  // them, and which reads as a broken auto-fill.
  const seeded: TradeFormValues = {
    accountId: s(t['accountId']),
    date: s(t['date']),
    contract: s(t['contract']),
    side: s(t['side']) === 'short' ? 'short' : 'long',
    qty: n(t['qty']) || 1,
    timeIn: s(t['timeIn']),
    timeOut: s(t['timeOut']),
    entry: n(t['entry']),
    exit: n(t['exit']),
    fees: n(t['fees']),
    feesAuto: t['feesAuto'] === true,
    riskPoints: plan.riskPoints,
    rrField: seedRrField(
      {
        rrPlanned: plan.rrPlanned,
        plannedTarget: t['plannedTarget'] == null ? null : n(t['plannedTarget']),
        riskPoints: plan.riskPoints,
      },
      t['entry'] == null ? null : n(t['entry']),
      unit,
    ),
    plannedStop: n(t['plannedStop']),
    plannedTarget: n(t['plannedTarget']),
    patternId: s(t['patternId']),
    exitModelId: s(t['exitModelId']),
    grade: s(t['grade']),
    sessionLabel: seedSessionLabel(s(t['sessionLabel']), s(t['date']), s(t['timeIn']), tz),
    marketRegime: s(t['marketRegime']),
    newsEvent: s(t['newsEvent']),
    notes: s(t['notes']),
    // ⚠ SEEDED FROM THE HYDRATED ROW, which is the whole permission slip for the
    // save owning these. `t` here is the full 48-column read (D46/D61), so the
    // draft starts as what is on disk and an untouched Save is a no-op.
    screenshots: Array.isArray(t['screenshots']) ? (t['screenshots'] as string[]) : [],
    recordings: Array.isArray(t['recordings']) ? (t['recordings'] as string[]) : [],
    booleans: (t['booleans'] && typeof t['booleans'] === 'object'
      ? { ...(t['booleans'] as Record<string, boolean>) } : {}),
    mistakeTags: Array.isArray(t['mistakeTags']) ? (t['mistakeTags'] as string[]) : [],
    tags: Array.isArray(t['tags']) ? (t['tags'] as string[]) : [],
    confluences: activeConfluenceIds(t['confluences']),
  };
  return applyInitialRiskFlow(seeded, unit, tickSize);
}

/** Sets do not JSON-stringify — flatten before comparing for the dirty check. */
function serialiseForDirty(v: TradeFormValues): unknown {
  return { ...v, confluences: [...v.confluences].sort() };
}

// =============================================================================
// Tabs
// =============================================================================

type Setter = <K extends keyof TradeFormValues>(k: K, v: TradeFormValues[K]) => void;
type Derived = ReturnType<typeof deriveTrade>;

interface Execution { time?: string; side?: string; qty?: number; price?: number }

function executionsOf(t: Row): readonly Execution[] {
  return Array.isArray(t['executions']) ? (t['executions'] as Execution[]) : [];
}

// ── Overview ────────────────────────────────────────────────────────────────

function OverviewTab(
  { v, d, trade, patterns }:
  { v: TradeFormValues; d: Derived; trade: Row; patterns: readonly PatternSummary[] | null },
) {
  const execs = executionsOf(trade);
  const exitSide = v.side === 'long' ? 'sell' : 'buy';
  const exitFills = execs.filter((e) => e.side === exitSide).length;
  const entryFills = execs.length - exitFills;
  const patternName = (patterns ?? []).find((p) => p.id === v.patternId)?.name ?? '';

  /**
   * ── 🚨 THE STATS AND THE PICTURE, SIDE BY SIDE — BETA'S `.tw-top` ──
   *
   * A chart image captured by the desktop logger IS the picture of the trade.
   * Beta pairs it with the numbers in a two-column grid the moment the trade
   * opens (index.html:2444); this client had it only in the attachment grid on
   * the sixth tab, which is a surface for ATTACHING files rather than for
   * looking at one. Josh reported it as the screenshots not displaying at all.
   *
   * ⚠ IT READS `v.screenshots` — THE DRAFT — not `trade`. Same rule the Notes
   * tab states: one source, so deleting an attachment there empties this panel
   * in the same render rather than leaving a picture of a file that is gone.
   *
   * ⚠ AND THE DRAFT IS SEEDED FROM THE HYDRATED ROW. The trades TABLE carries
   * 23 of 48 columns and `screenshots` is deliberately not among them, so a
   * panel fed from that projection would be permanently empty — and would look
   * exactly like a trade with no captures.
   */
  return (
    <div className="tw-top">
      <div className="tw-stats-body">
        <div className="tw-npl-l">Net P&amp;L</div>
      <div className={`tw-npl ${d.netPnl >= 0 ? 'green' : 'red'}`}>{money(d.netPnl)}</div>
      <StatRow k="Side" v={v.side === 'long' ? 'LONG' : 'SHORT'} cls={v.side === 'long' ? 'green' : 'red'} />
      <StatRow k="Contracts traded" v={String(v.qty)} />
      {/* ⚠ "Avg Entry" only when more than one fill went into it. On a single-fill
          trade the average IS the price, and calling it an average implies a
          scale-in that never happened. */}
      <StatRow
        k={entryFills > 1 ? 'Avg Entry' : 'Entry'}
        v={v.entry ? num(v.entry, 2) : '—'}
        sub={entryFills > 1 ? `avg of ${entryFills} fills` : ''}
      />
      <StatRow
        k={exitFills > 1 ? 'Avg Exit' : 'Exit'}
        v={v.exit ? num(v.exit, 2) : '—'}
        sub={exitFills > 1 ? `avg of ${exitFills} fills` : ''}
      />
      <StatRow
        k="Points" v={`${d.points >= 0 ? '+' : ''}${num(d.points, 2)}`}
        cls={d.points > 0 ? 'green' : d.points < 0 ? 'red' : ''}
      />
        <StatRow k="Pattern" v={patternName || '—'} />
        <StatRow k="Planned R" v={v.rrField ? `${num(v.rrField, 2)}R` : '—'} />
        <StatRow
          k="Realized R" v={d.rrRealized != null ? `${num(d.rrRealized, 2)}R` : '—'}
          cls={(d.rrRealized ?? 0) > 0 ? 'green' : (d.rrRealized ?? 0) < 0 ? 'red' : ''}
        />
      </div>
      <ShotCarousel files={v.screenshots} />
    </div>
  );
}

function StatRow({ k, v, cls, sub }: { k: string; v: string; cls?: string; sub?: string }) {
  return (
    <div className="tw-row">
      <span className="tw-k">{k}</span>
      <span className={`tw-v ${cls ?? ''}`}>
        {v}
        {sub ? <span className="tw-sub">{sub}</span> : null}
      </span>
    </div>
  );
}

// ── Execution ───────────────────────────────────────────────────────────────

function ExecutionTab(
  { v, d, trade, set, editPlan, accounts, contracts }: {
    v: TradeFormValues; d: Derived; trade: Row; set: Setter;
    editPlan: (p: Partial<TradeFormValues>) => void;
    accounts: readonly { id: string; name: string; archived?: boolean; externalId?: string }[] | null;
    contracts: readonly { symbol: string; name: string }[] | null;
  },
) {
  const execs = executionsOf(trade);
  // Active accounts first, archived below and labelled — the old client's
  // `orderAccountsForPicker`. A wall of blown evaluations above the live account
  // is the same defect the filter drawer's screenshot caught.
  const ordered = [...(accounts ?? [])].sort((a, b) =>
    Number(!!a.archived) - Number(!!b.archived) || a.name.localeCompare(b.name));

  return (
    <>
      <SectionHead>Core Execution</SectionHead>
      <div className="form-grid cols-3">
        <Field label="Account">
          <select value={v.accountId} onChange={(e) => set('accountId', e.target.value)}>
            <option value="">(unassigned)</option>
            {ordered.map((a) => (
              <option key={a.id} value={a.id}>
                {(a.name || a.externalId || '(unnamed)') + (a.archived ? ' · archived' : '')}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Date">
          <input type="date" value={v.date} onChange={(e) => set('date', e.target.value)} />
        </Field>
        <Field label="Contract">
          <select value={v.contract} onChange={(e) => editPlan({ contract: e.target.value })}>
            <option value="">—</option>
            {(contracts ?? []).map((c) => (
              <option key={c.symbol} value={c.symbol}>{c.symbol} — {c.name}</option>
            ))}
            {/* ⚠ A stored contract the catalogue cannot name is still offered, by
                symbol. Without it the select falls back to "—" on a real trade
                and the next save clears the instrument. */}
            {v.contract && !(contracts ?? []).some((c) => c.symbol === v.contract) ? (
              <option value={v.contract}>{v.contract}</option>
            ) : null}
          </select>
        </Field>
        <Field label="Side">
          <select
            value={v.side}
            onChange={(e) => editPlan({ side: e.target.value === 'short' ? 'short' : 'long' })}
          >
            <option value="long">Long</option>
            <option value="short">Short</option>
          </select>
        </Field>
        <NumField label="Qty (contracts)" value={v.qty} min={1} onChange={(x) => editPlan({ qty: x })} />
        <Field label="Time In">
          <input type="time" step={1} value={v.timeIn} onChange={(e) => set('timeIn', e.target.value)} />
        </Field>
        <Field label="Time Out">
          <input type="time" step={1} value={v.timeOut} onChange={(e) => set('timeOut', e.target.value)} />
        </Field>
        <NumField label="Entry Price" value={v.entry} onChange={(x) => editPlan({ entry: x })} />
        <NumField label="Exit Price" value={v.exit} onChange={(x) => set('exit', x)} />
        {/* ⚠ Typing a fee switches OFF the commission-schedule auto-fill for this
            trade, exactly as the old client does — otherwise the read-side heal
            (D59) would keep overwriting a correction the user made by hand. */}
        <NumField
          label="Fees $" value={v.fees}
          onChange={(x) => { set('fees', x); set('feesAuto', false); }}
        />
        <Field label="Points (auto)"><input value={num(d.points, 4)} disabled /></Field>
        <Field label="Net P&L (auto)">
          <input className={d.netPnl >= 0 ? 'green' : 'red'} value={money(d.netPnl)} disabled />
        </Field>
      </div>

      {/* ⚠ Only when the trade was actually scaled. One fill is not a breakdown. */}
      {execs.length > 1 ? (
        <>
          <SectionHead>Fills</SectionHead>
          <div className="tw-xhd">
            {execs.length} executions · entry &amp; exit are volume-weighted averages
          </div>
          <div className="tw-xrow tw-xhead">
            <span>Time</span><span>Action</span><span>Qty</span><span>Price</span><span>Pos</span>
          </div>
          {runningPositions(execs).map((r, i) => (
            <div className="tw-xrow" key={`${r.time}-${i}`}>
              <span className="tw-xt">{r.time || '—'}</span>
              <span className={`tw-xa ${r.buy ? 'buy' : 'sell'}`}>{r.buy ? 'BUY' : 'SELL'}</span>
              <span className="tw-xq">{r.qty}</span>
              <span className="tw-xp">{num(r.price, 2)}</span>
              <span className="tw-xn">{r.pos > 0 ? `+${r.pos}` : r.pos}</span>
            </div>
          ))}
        </>
      ) : null}
    </>
  );
}

/** Running net contracts after each fill — flat when it returns to 0. */
function runningPositions(execs: readonly Execution[]) {
  let pos = 0;
  return execs.map((e) => {
    const q = Number(e.qty) || 0;
    const buy = e.side === 'buy';
    pos += buy ? q : -q;
    return { time: e.time ?? '', buy, qty: q, price: Number(e.price) || 0, pos };
  });
}

// ── Risk ────────────────────────────────────────────────────────────────────

function RiskTab(
  { v, d, trade, unit, editPlan, editStop, editTarget, flipUnit, dpp }: {
    v: TradeFormValues; d: Derived; trade: Row; unit: PlannedTargetUnit;
    editPlan: (p: Partial<TradeFormValues>) => void;
    editStop: (x: number) => void; editTarget: (x: number) => void;
    flipUnit: (to: PlannedTargetUnit) => void;
    dpp: number;
  },
) {
  const mae = trade['maePoints'] == null ? null : n(trade['maePoints']);
  const mfe = trade['mfePoints'] == null ? null : n(trade['mfePoints']);
  const hasExcursion = mae != null || mfe != null;

  // Which contract's tape the excursion was measured from, and what was traded.
  // Compared case-insensitively as plain strings: ANY difference is worth saying,
  // so this needs no price-equivalence rule (and `ui/` may not reach for one).
  const excursionSource = String(trade['excursionSource'] ?? '').toUpperCase();
  const tradedContract = String(trade['contractFull'] ?? trade['contract'] ?? '').toUpperCase();

  const ex = excursionDisplay({
    maePoints: mae, mfePoints: mfe,
    maeSecs: trade['maeSecs'] == null ? null : n(trade['maeSecs']),
    mfeSecs: trade['mfeSecs'] == null ? null : n(trade['mfeSecs']),
    riskPoints: excursionRiskPoints(v.riskPoints, v.entry, v.plannedStop),
    dollarsPerPoint: dpp, qty: v.qty, realizedPoints: d.points,
  });

  return (
    <>
      <SectionHead>Risk Plan</SectionHead>
      <p className="muted field-hint">
        Enter <b>Risk (pts)</b> and <b>R:R Planned</b>; Stop and Target auto-fill from your
        Entry and Side. Override Stop or Target directly if needed — the others sync.
      </p>
      <div className="form-grid cols-3">
        <NumField
          label="Risk (pts)" value={v.riskPoints} placeholder="e.g. 3.25"
          onChange={(x) => editPlan({ riskPoints: x })}
        />
        <Field
          htmlFor="tf-rrp"
          label={unit === 'points' ? 'Reward (pts)' : 'R:R Planned'}
          after={
            <span className="rrp-unit-toggle" title="Type your target as an R:R ratio or as a points distance">
              <button type="button" className={unit === 'r' ? 'on' : ''} onClick={() => flipUnit('r')}>R</button>
              <button type="button" className={unit === 'points' ? 'on' : ''} onClick={() => flipUnit('points')}>pts</button>
            </span>
          }
        >
          <input
            id="tf-rrp" type="number" step="any" value={v.rrField || ''}
            placeholder={unit === 'points' ? 'e.g. 8' : 'e.g. 2'}
            onChange={(e) => editPlan({ rrField: Number(e.target.value) || 0 })}
          />
        </Field>
        <NumField label="Planned Stop (auto)" value={v.plannedStop} onChange={editStop} />
        <NumField label="Planned Target (auto)" value={v.plannedTarget} onChange={editTarget} />
        <Field label="Risk $ at Entry (auto)">
          <input value={d.riskAtEntry == null ? '' : d.riskAtEntry.toFixed(2)} disabled />
        </Field>
        <Field label="R:R Realized (auto)">
          <input
            className={(d.rrRealized ?? 0) > 0 ? 'green' : (d.rrRealized ?? 0) < 0 ? 'red' : ''}
            value={d.rrRealized == null ? '' : d.rrRealized.toFixed(2)} disabled
          />
        </Field>
      </div>

      {/* ⚠ READ-ONLY, and only the POINTS are facts. The $ tracks qty and
          contract, the R tracks the risk being typed above, and MFE Captured
          tracks the exit — so all three recompute rather than render once. */}
      {hasExcursion ? (
        <>
          <SectionHead>
            Excursion <span className="muted normal">(auto from logger)</span>
          </SectionHead>
          {/* 🚨 WHOSE TAPE THESE NUMBERS CAME FROM, when it was not this contract's.
              On a cross-traded book the chart and the data feed are the MINI while
              the order fills as the MICRO, so the excursion is genuinely measured
              from a neighbouring contract (REBUILD_DECISIONS D176). They track the
              same price, so the figures are right — but "right" and "measured from
              the thing you traded" are different claims, and only one of them is
              true here.
              ⚠ Absent means the source never said, NOT that it was this contract.
              So this renders only when there is something to state, and never
              asserts a source it does not have — which is D61 exactly: do not
              render a field you have not read. */}
          {excursionSource && excursionSource !== tradedContract ? (
            <p className="muted field-hint">
              Measured from <b>{excursionSource}</b>, not {tradedContract || 'this contract'} —
              they track the same price, and it is your data feed that carries{' '}
              {excursionSource}.
            </p>
          ) : null}
          <div className="form-grid cols-3">
            <Field label="Max Adverse (MAE)"><input className="red" value={ex.mae} disabled /></Field>
            <Field label="Max Favorable (MFE)"><input className="green" value={ex.mfe} disabled /></Field>
            <Field label="MFE Captured">
              <input
                className={ex.capturedPct != null && ex.capturedPct < 0 ? 'red' : ''}
                value={ex.captured} disabled
              />
            </Field>
          </div>
          <Field label="Excursion Timing (from entry)">
            <input value={ex.timing} disabled />
          </Field>
        </>
      ) : null}
    </>
  );
}

// ── Setup ───────────────────────────────────────────────────────────────────

function SetupTab(
  { v, set, patterns, exitModels, exitModelsPhase, registry }: {
    v: TradeFormValues; set: Setter;
    patterns: readonly PatternSummary[] | null;
    exitModels: readonly ExitModelSummary[] | null;
    exitModelsPhase: 'loading' | 'ready' | 'failed';
    registry: readonly ConfluenceNode[];
  },
) {
  const pickable = taggablePatterns(patterns);

  return (
    <>
      <SectionHead>Setup &amp; Context</SectionHead>
      <div className="form-grid cols-2 pattern-card">
        <Field label="Pattern">
          <select value={v.patternId} onChange={(e) => set('patternId', e.target.value)}>
            <option value="">(none)</option>
            {pickable.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            {/* ⚠ A stored pattern this picker would not otherwise offer — a
                candidate, or one the read has not landed — is still listed, by
                id. Without it the select falls back to "(none)" on a tagged trade
                and the next save clears a link the user never touched. */}
            {v.patternId && !pickable.some((p) => p.id === v.patternId) ? (
              <option value={v.patternId}>
                {(patterns ?? []).find((p) => p.id === v.patternId)?.name ?? `(pattern ${v.patternId})`}
              </option>
            ) : null}
          </select>
        </Field>
        <div className="field">
          <span className="field-label">Entry Criteria</span>
          {/* ⚠ NOT PORTED, and said so. The checklist resolves against pattern
              rule VERSIONS — pinned per trade — and this client does not load
              them. Ticking a criterion against the wrong version would attribute
              a trade to rules it was never judged by. Existing marks survive
              untouched: the form never sends `criteriaMet`. */}
          <p className="muted crit-empty">
            {v.patternId
              ? 'The entry-criteria checklist isn’t ported yet — it resolves against pattern rule versions this client doesn’t load. Any criteria already ticked on this trade are preserved.'
              : 'Select a pattern to check its criteria.'}
          </p>
        </div>
      </div>

      <div className="form-grid cols-3">
        <Field label="Exit Model">
          <select
            value={v.exitModelId}
            onChange={(e) => set('exitModelId', e.target.value)}
            disabled={exitModelsPhase === 'failed'}
          >
            <option value="">(none)</option>
            {(exitModels ?? []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            {v.exitModelId && !(exitModels ?? []).some((m) => m.id === v.exitModelId) ? (
              <option value={v.exitModelId}>{`(exit model ${v.exitModelId})`}</option>
            ) : null}
          </select>
          {/* ⚠ Two DIFFERENT states, and conflating them sends someone off to
              create exit models they already have. */}
          {exitModelsPhase === 'failed' ? (
            <span className="field-hint red">Couldn’t load your exit models — this is a read problem, not an empty list.</span>
          ) : exitModels && exitModels.length === 0 ? (
            <span className="field-hint muted">
              No exit models yet. The editor isn’t ported, so they have to be created in the
              current app — and one is required whenever a Pattern is set.
            </span>
          ) : null}
        </Field>
        <Field label={<>Execution Grade <span className="muted italic">(how you executed)</span></>}>
          <select value={v.grade} onChange={(e) => set('grade', e.target.value)}>
            <option value="">—</option>
            {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </Field>
        <Field label="Session">
          <select value={v.sessionLabel} onChange={(e) => set('sessionLabel', e.target.value)}>
            <option value="">—</option>
            {SESSIONS.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </Field>
        <Field label="Market Regime">
          <select value={v.marketRegime} onChange={(e) => set('marketRegime', e.target.value)}>
            <option value="">—</option>
            {REGIMES.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </Field>
        <Field label="News Event">
          <input
            value={v.newsEvent} placeholder="CPI / FOMC / —"
            onChange={(e) => set('newsEvent', e.target.value)}
          />
        </Field>
      </div>

      <SectionHead>Confluences</SectionHead>
      <p className="muted field-hint">
        Check the tools that confirmed the setup. Tools with sub-confluences (▸) expand on
        click — pick the specifics and the parent tags itself.
      </p>
      <ConfluenceGrid registry={registry} v={v} set={set} />
    </>
  );
}

function ConfluenceGrid(
  { registry, v, set }:
  { registry: readonly ConfluenceNode[]; v: TradeFormValues; set: Setter },
) {
  /**
   * ── 🚨 EXPANSION IS DERIVED, NOT SEEDED, AND THE SEEDED VERSION WAS A BUG ──
   *
   * This used to be `useState(() => …)` computing the open set from the ticked
   * children. A lazy initialiser runs ONCE, on the first render — and on that
   * render BOTH of its inputs are empty: `registry` arrives with the preferences
   * read, and `v.confluences` arrives with the trade hydration. So it seeded from
   * nothing and never recomputed.
   *
   * The symptom Josh reported: **the trade form shows only top-level
   * confluences and never the children.** Worse than cosmetic — a trade with
   * three sub-confluences ticked displayed one checked parent and no way to see
   * which three. The marks were stored, counted in analytics, and invisible.
   *
   * D86 again, in a third shape: state seeded from data that arrives later. The
   * fix is the same one every time — derive it, do not store it.
   *
   * `override` holds only what the USER has explicitly opened or closed, so a
   * parent that auto-opens because a child is ticked can still be collapsed.
   */
  const [override, setOverride] = useState<ReadonlyMap<string, boolean>>(new Map());

  const autoOpen = (c: ConfluenceNode) =>
    v.confluences.has(c.id) || c.children.some((ch) => v.confluences.has(ch.id));

  const toggle = (id: string) => {
    const next = new Set(v.confluences);
    if (next.has(id)) next.delete(id); else next.add(id);
    set('confluences', next);
  };

  return (
    <div className="confluence-grid">
      {registry.map((c) => {
        const kidsOn = c.children.filter((ch) => v.confluences.has(ch.id)).length;
        // ⚠ DERIVED for a parent WITH children: it is checked when any child is,
        // and the old client makes that checkbox unclickable for the same reason.
        const on = v.confluences.has(c.id) || kidsOn > 0;
        // Derived, with the user's own choice on top. See `override` above.
        const expanded = override.get(c.id) ?? autoOpen(c);
        const flip = () => setOverride((m) => new Map(m).set(c.id, !expanded));
        return (
          <div key={c.id}>
            {/* ⚠ THE WHOLE ROW EXPANDS, not just the chevron — beta puts
                `cursor:pointer` on the row and its comment says clicking anywhere
                opens it. A 12px chevron is a poor target for the primary gesture
                on this control. */}
            <div
              className={`confluence-row${on ? ' checked' : ''}${c.children.length ? ' conf-parent' : ''}`}
              onClick={c.children.length ? flip : undefined}
            >
              {/* ── 🚨 A PARENT HAS NO `<label>`, AND THAT IS THE WHOLE FIX ──
                  The first version wrapped both rows the same way and stopped
                  the label's click from reaching the row, so the checkbox would
                  not also expand. But the label is `flex: 1` — it FILLS the row.
                  So the only clickable part left was the few pixels of padding
                  above and below it, and Josh found that by luck.

                  Beta does not wrap a parent at all: the checkbox is inert
                  (`pointer-events: none`, `tabindex="-1"`) because it is derived
                  from the children, and the row itself is the target. Only a
                  LEAF gets a label, because only a leaf has a box worth
                  clicking. */}
              {c.children.length ? (
                <>
                  <input
                    type="checkbox" checked={on} readOnly tabIndex={-1}
                    aria-label={c.label} className="conf-derived"
                  />
                  <span className="conf-name" title={c.hint ?? ''}>{c.label}</span>
                  <span className="grow" />
                </>
              ) : (
                <label>
                  <input type="checkbox" checked={on} onChange={() => toggle(c.id)} />
                  <span className="conf-name" title={c.hint ?? ''}>{c.label}</span>
                </label>
              )}
              {c.children.length ? (
                <button
                  type="button" className="conf-chevron"
                  aria-expanded={expanded}
                  aria-label={`${expanded ? 'Collapse' : 'Expand'} ${c.label}`}
                  onClick={(e) => { e.stopPropagation(); flip(); }}
                >{expanded ? '▾' : '▸'}</button>
              ) : null}
            </div>
            {c.children.length && expanded ? (
              <div className="conf-children">
                {c.children.map((ch) => (
                  <label key={ch.id} className="conf-child">
                    <input
                      type="checkbox" checked={v.confluences.has(ch.id)}
                      onChange={() => toggle(ch.id)}
                    />
                    <span>{ch.label}</span>
                  </label>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
      <p className="muted field-hint">
        Editing this list lives in Settings and isn’t ported. A confluence removed there
        stays on the trades that used it — this form carries it through rather than
        deleting history it can’t display.
      </p>
    </div>
  );
}

// ── Process ─────────────────────────────────────────────────────────────────

function ProcessTab(
  { v, d, set, prefs }: {
    v: TradeFormValues; d: Derived; set: Setter;
    prefs: { customMistakeTags?: string[]; customTags?: unknown[] } | null;
  },
) {
  const isLoss = d.netPnl < 0;
  const vocabulary = prefs?.customMistakeTags ?? [];
  // ⚠ THE SAME parser the filter drawer uses. Two of them would disagree about
  // which tags exist on some edge case — an entry with no name, a numeric id —
  // and you would be able to apply a tag here that the filter never offers.
  const registry = parseTagRegistry(prefs?.customTags);

  const toggleBool = (key: string) => (e: ChangeEvent<HTMLInputElement>) => {
    // ⚠ An unchecked box is DELETED, never stored as `false`. An explicit false
    // counts as a good-direction vote in the discipline score, so writing one for
    // every unchecked box scores a trade from judgments nobody made.
    const next = { ...v.booleans };
    if (e.target.checked) next[key] = true; else delete next[key];
    set('booleans', next);
  };

  const toggleTag = (tag: string) => {
    set('mistakeTags', v.mistakeTags.includes(tag)
      ? v.mistakeTags.filter((x) => x !== tag)
      : [...v.mistakeTags, tag]);
  };

  return (
    <>
      <SectionHead>Process</SectionHead>
      <div className="boolean-grid">
        {TRADE_BOOLEANS
          // ⚠ Loss-only, and NOT RENDERING it also means not SENDING it — a flag
          // stored on a trade that later became a winner survives untouched.
          .filter((b) => !b.lossOnly || isLoss)
          .map((b) => (
            <label key={b.key} className="filter-opt">
              <input
                type="checkbox" checked={v.booleans[b.key] === true}
                onChange={toggleBool(b.key)}
              />
              <span className="filter-opt-label">{b.label}</span>
            </label>
          ))}
      </div>
      {/* ⚠ THE DIRECTION IS STATED. Every box means something WENT WRONG, and a
          user who reads them as positives inverts their own discipline score with
          nothing on screen looking off. */}
      <p className="muted field-hint">
        Check only what went wrong. Anything left unchecked means that expectation held.
      </p>

      <Field label="Mistake Tags (multi)">
        <div className="toggle-group">
          {vocabulary.length ? vocabulary.map((tag) => (
            <button
              key={tag} type="button"
              className={v.mistakeTags.includes(tag) ? 'active' : ''}
              aria-pressed={v.mistakeTags.includes(tag)}
              onClick={() => toggleTag(tag)}
            >{tag}</button>
          )) : <span className="muted">Add tag vocabulary in Settings.</span>}
        </div>
      </Field>

      <Field label="Tags">
        <TagField v={v} set={set} registry={registry} />
      </Field>
    </>
  );
}

function TagField(
  { v, set, registry }: { v: TradeFormValues; set: Setter; registry: readonly UserTag[] },
) {
  const [q, setQ] = useState('');
  const applied = new Set(v.tags);
  // ⚠ Only ids that still RESOLVE are shown as chips, matching `tradeTagIds`.
  // An id whose tag was deleted from the registry renders as nothing here — and
  // is preserved anyway, because removal only ever filters the list explicitly.
  const chips = v.tags.map((id) => registry.find((t) => t.id === id)).filter((t): t is UserTag => !!t);
  const matches = registry.filter(
    (t) => !applied.has(t.id) && (!q || t.name.toLowerCase().includes(q.toLowerCase())),
  );

  return (
    <div className="utag-field">
      <div className="utag-chips">
        {chips.length ? chips.map((t) => (
          <span key={t.id} className="utag-chip">
            {t.name}
            <button
              type="button" aria-label={`Remove ${t.name}`}
              onClick={() => set('tags', v.tags.filter((x) => x !== t.id))}
            >×</button>
          </span>
        )) : <span className="muted">No tags</span>}
      </div>
      <input
        type="text" value={q} placeholder="Add a tag…"
        onChange={(e) => setQ(e.target.value)}
      />
      {q && matches.length ? (
        <div className="utag-menu">
          {matches.slice(0, 8).map((t) => (
            <button
              key={t.id} type="button"
              onClick={() => { set('tags', [...v.tags, t.id]); setQ(''); }}
            >{t.name}</button>
          ))}
        </div>
      ) : null}
      {/* ⚠ Beta's placeholder reads "Add or create a tag…". CREATE is not ported:
          it writes the registry on `user_preferences`, a different table, and a
          half-written registry entry would leave an id on the trade that resolves
          to nothing. Said rather than silently missing. */}
      <span className="field-hint muted">
        Existing tags only — creating a new one writes the tag registry, which isn’t ported.
      </span>
    </div>
  );
}

// ── Notes & Media ───────────────────────────────────────────────────────────

// ⚠ NO `trade` PROP ANY MORE, and its removal is the point: this tab used to
// read `trade['screenshots']` straight off the raw row because the form did not
// model the lists. Now it does, so everything here comes from `v` — the draft —
// and there is no second source that could disagree with what Save writes.
function NotesTab({ v, set }: { v: TradeFormValues; set: Setter }) {
  // ⚠ THE STEMS ARE BETA'S, VERBATIM (index.html:26440-26442) — `date-contract`
  // for images and the same with `-rec` for audio. It matters because these
  // buckets have no backup, so recovering an object means reading the filenames,
  // and one client naming things differently makes that job guesswork. The
  // fallback is `trade` when the contract is not typed yet, also beta's.
  //
  // ⚠ NOT the trade id. A first version used one; beta does not, and the id is
  // in the object name the LOGGER writes (`…-auto-<idfrag>.jpg`) precisely
  // because those are machine-generated and this one is not.
  const stem = `${String(v.date || 'undated')}-${String(v.contract || 'trade')}`;

  return (
    <>
      <SectionHead>Notes</SectionHead>
      <textarea
        rows={5} value={v.notes} aria-label="Notes"
        placeholder="What happened, what I saw, what I'd change"
        onChange={(e) => set('notes', e.target.value)}
      />

      <SectionHead>Screenshots</SectionHead>
      {/* ⚠ BOTH LISTS ARE NOW PART OF THE DRAFT, which is what made it legal for
          them to join `OWNED_FIELDS` in saveTrade. The form hydrates the whole
          row before it renders a field (D61), so the list here IS the list on
          disk — add and remove edit it, and Save writes it back.

          ⚠ The image UPLOADS immediately; the filename is a draft edit until
          Save. Closing the form without saving therefore leaves an orphan blob
          and no reference, which is the safe direction: nothing is destroyed. */}
      <ScreenshotGrid
        files={v.screenshots}
        prefix={stem}
        onChange={(next) => set('screenshots', next)}
      />
      <span className="field-hint muted">
        Click, paste or drop. The image uploads at once — <b>Save</b> attaches it to the
        trade. Removing a thumbnail un-attaches it; the file itself is kept.
      </span>

      <SectionHead>Voice Notes</SectionHead>
      {/* ⚠ NO CONFIRM ON REMOVE HERE, unlike the journal's, and deliberately: this
          is a draft edit, so closing the form without saving is already the undo.
          The journal's writes at once and has no undo but the confirm. */}
      <VoiceNotes
        files={v.recordings}
        prefix={`${stem}-rec`}
        onChange={(next) => set('recordings', next)}
      />
      <span className="field-hint muted">
        Recorded audio uploads at once; <b>Save</b> attaches it.
      </span>
    </>
  );
}

// =============================================================================
// Complete Annotations
// =============================================================================

/**
 * The old client relocates the real controls into an overlay so every handler
 * keeps working. React does not need the trick — the same components render here
 * against the same state — but the BEHAVIOUR is ported exactly: only unfilled
 * fields appear, the optional sections always do, and Done marks it reviewed
 * without saving.
 */
function CompleteAnnotationsOverlay(
  { v, d, set, editPlan, unit, patterns, exitModels, registry, prefs, onSkip, onDone }: {
    v: TradeFormValues; d: Derived; set: Setter;
    editPlan: (p: Partial<TradeFormValues>) => void;
    unit: PlannedTargetUnit;
    patterns: readonly PatternSummary[] | null;
    exitModels: readonly ExitModelSummary[] | null;
    registry: readonly ConfluenceNode[];
    prefs: { customMistakeTags?: string[]; customTags?: unknown[] } | null;
    onSkip: () => void; onDone: () => void;
  },
) {
  // ── 🔴 THE LIST IS FROZEN AT OPEN. THAT IS THE WHOLE FIX (TCR-91) ──
  //
  // Josh: *"Each field in the quick annotation form disappears quickly with one
  // selection or one character types."* `unfilledRequirements` was being
  // re-derived on every render and each field rendered only while its
  // requirement was still unmet — so one character satisfied
  // `notes.trim().length > 0`, the textarea UNMOUNTED WITH THE CARET IN IT, and
  // the sentence could not be finished. One pick did the same to every dropdown.
  //
  // ⚠ Two lists, deliberately, and keeping them apart is the design:
  //
  //   `frozen`    which rows RENDER — captured once, when the overlay opened
  //   `unfilled`  which rows are DONE, and the counter — live, every render
  //
  // ✅ A `useState` initialiser is enough because the overlay is mounted fresh
  // each time: `{overlayOpen ? <CompleteAnnotationsOverlay … /> : null}`. No
  // effect, no ref, no key.
  const [frozen] = useState<readonly string[]>(
    () => unfilledRequirements(v).map((r) => r.key),
  );
  const unfilled = unfilledRequirements(v);
  const total = ANNOTATION_REQUIREMENTS.length;
  // ⚠ OUT OF SIX, not out of `frozen`. Opening with one requirement already met
  // must read "1 of 6 done" — re-basing it on what is on screen would be a
  // different and wrong statement about the trade.
  const done = total - unfilled.length;
  const need = (k: string) => frozen.includes(k);
  const isDone = (k: string) => !unfilled.some((r) => r.key === k);

  /**
   * Wrap a requirement row so a finished one is marked rather than removed.
   *
   * 🔴 A PLAIN FUNCTION, CALLED — NOT A COMPONENT RENDERED AS `<Row/>`. A
   * component defined inside this render would be a new type on every render, so
   * React would unmount and remount its children on each keystroke — which is
   * the exact defect this card exists to fix, reintroduced by the fix. Called as
   * a function, the elements it returns are just part of this tree.
   */
  const row = (k: string, children: ReactNode) => (!need(k) ? null : (
    <div className={`tw2-annrow${isDone(k) ? ' done' : ''}`} key={k}>
      {isDone(k) ? <span className="tw2-anntick" aria-label="Done">✓</span> : null}
      {children}
    </div>
  ));

  const pickable = taggablePatterns(patterns);

  return (
    <div className="tw2-annov" role="dialog" aria-label="Complete annotations">
      <div className="tw2-anncard">
        <div className="tw2-annhd">
          <b>Complete Annotations</b>
          <span className="muted">{done} of {total} done</span>
          <div className="tw2-annprog"><div style={{ width: `${Math.round((done / total) * 100)}%` }} /></div>
        </div>

        <div className="tw2-annbody">
          {!unfilled.length ? (
            <div className="tw2-anndone">
              <b>All set.</b>
              <div className="muted">Every required field is filled in. Tag a mistake below if this one had one.</div>
            </div>
          ) : null}

          {row('risk', (
            <div className="form-grid cols-2">
              <NumField label="Risk (pts)" value={v.riskPoints} onChange={(x) => editPlan({ riskPoints: x })} />
              <NumField
                label={unit === 'points' ? 'Reward (pts)' : 'R:R Planned'}
                value={v.rrField} onChange={(x) => editPlan({ rrField: x })}
              />
            </div>
          ))}

          {row('pattern', (
            <Field label="Pattern">
              <select value={v.patternId} onChange={(e) => set('patternId', e.target.value)}>
                <option value="">(none)</option>
                {pickable.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
          ))}

          {row('exit', (
            <Field label="Exit Model">
              <select value={v.exitModelId} onChange={(e) => set('exitModelId', e.target.value)}>
                <option value="">(none)</option>
                {(exitModels ?? []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </Field>
          ))}

          {row('grade', (
            <Field label="Execution Grade">
              <select value={v.grade} onChange={(e) => set('grade', e.target.value)}>
                <option value="">—</option>
                {GRADES.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </Field>
          ))}

          {row('confluences', (
            <>
              <SectionHead>Confluences</SectionHead>
              <ConfluenceGrid registry={registry} v={v} set={set} />
            </>
          ))}

          {row('notes', (
            <Field label="Notes">
              <textarea
                rows={4} value={v.notes} aria-label="Notes"
                placeholder="What happened, what I saw, what I'd change"
                onChange={(e) => set('notes', e.target.value)}
              />
            </Field>
          ))}

          {/* ⚠ ALWAYS shown, and deliberately NOT required. A clean trade breaches
              nothing and has no mistake — gating the ✓ on either would nag forever
              on exactly the trades that went right. */}
          <SectionHead>Discipline — flag any breach (optional)</SectionHead>
          <ProcessTab v={v} d={d} set={set} prefs={prefs} />
        </div>

        <div className="tw2-annfoot import-row">
          <button type="button" className="btn" onClick={onSkip}>Skip for now</button>
          <span className="grow" />
          <button type="button" className="btn primary" onClick={onDone}>Done — back to trade</button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Small shared pieces
// =============================================================================


function SectionHead({ children }: { children: ReactNode }) {
  return <h4 className="tw2-head">{children}</h4>;
}

/**
 * A labelled control.
 *
 * ── 🚨 WHY `after` CHANGES THE WRAPPER FROM <label> TO <div> ──
 *
 * `<button>` is a LABELABLE element in HTML. So the first version — which put the
 * R/pts unit toggle inside the field's `<label>`, beside the text — made the `R`
 * BUTTON the label's control instead of the input, because the label binds to its
 * first labelable descendant.
 *
 * The consequences were real, not cosmetic: clicking the words "Reward (pts)"
 * activated the R button and silently flipped the unit, and the R:R field could
 * not be reached by its own label at all. A test caught it by writing 9 into what
 * it believed was the input and getting a saved R:R of 2 — the value was landing
 * on a button.
 *
 * So a field WITH a trailing control uses an explicit `htmlFor`/`id` pair and no
 * wrapping label. Fields without one keep the simpler implicit form.
 */
function Field(
  { label, children, after, htmlFor }:
  { label: ReactNode; children: ReactNode; after?: ReactNode; htmlFor?: string },
) {
  if (after) {
    return (
      <div className="field">
        <span className="field-label">
          <label htmlFor={htmlFor}>{label}</label>{after}
        </span>
        {children}
      </div>
    );
  }
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}

function NumField(
  { label, value, onChange, min, placeholder }: {
    label: ReactNode; value: number; onChange: (x: number) => void;
    min?: number; placeholder?: string;
  },
) {
  return (
    <Field label={label}>
      <input
        type="number" step="any" min={min}
        // ⚠ Empty string for 0, so a cleared field does not immediately refill
        // with "0" under the cursor — and so a genuinely unset risk reads as
        // unset rather than as zero risk.
        value={value || ''}
        placeholder={placeholder}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
      />
    </Field>
  );
}
